import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDelete, Field, PageHeader, Panel } from '@/components/kit';
import { FormDialog, ListBody, SwitchRow } from '@/components/inputs';
import { PaymentInstructionsCard } from '@/components/PaymentInstructions';
import {
  PAYMENT_METHOD_HINTS,
  hasDetails,
  loadPaymentSettings,
  safeLink,
  withSettings,
} from '@/shared/paymentSettings';
import { committed, logActivity } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';

export const route = {
  path: '/admin/payment-methods',
  layout: 'owner',
  access: "['Admin_c','Staff_c'].includes(profile)",
};
export const nav = { icon: 'Wallet', label: 'Payment methods', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 41 };

const EMPTY = { payTo: '', link: '', instructions: '', active: true };

function MethodDialog({ open, onOpenChange, method, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const setting = method?.setting;
  const hints = PAYMENT_METHOD_HINTS[method?.value] ?? {};

  useEffect(() => {
    if (open) {
      setForm({
        payTo: setting?.pay_to_c ?? '',
        link: setting?.link_c ?? '',
        instructions: setting?.instructions_c ?? '',
        active: setting ? setting.active_c !== false : true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const link = form.link.trim();
  const linkBad = !!link && !safeLink(link);

  async function submit() {
    if (linkBad) return;
    const fields = {
      pay_to_c: form.payTo.trim(),
      link_c: link,
      instructions_c: form.instructions.trim(),
      active_c: !!form.active,
    };
    setSaving(true);
    try {
      const res = setting
        ? await sdk.table('payment_settings_c').update({ Id: setting.Id, ...fields })
        : await sdk.table('payment_settings_c').create({ Name: method.value, ...fields });
      if (!committed(res, `${method.label} saved`)) return;
      logActivity('Payment', `Updated ${method.label} payment details`, method.value);
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${method?.label ?? 'Payment'} details`}
      description="Customers see these on the order confirmation and on their order page until it is paid."
      onSubmit={submit}
      saving={saving}
      submitLabel="Save details"
      wide
    >
      <Field label="Pay to" htmlFor="pm-payto" hint="Email, handle, wallet address or account name.">
        <Input id="pm-payto" value={form.payTo} onChange={(e) => set('payTo')(e.target.value)} placeholder={hints.payTo || 'Where the money goes'} />
      </Field>
      <Field label="Payment link" htmlFor="pm-link" hint="Optional. Must start with https://">
        <Input
          id="pm-link"
          type="url"
          value={form.link}
          onChange={(e) => set('link')(e.target.value)}
          placeholder={hints.link || 'https://…'}
          aria-invalid={linkBad || undefined}
        />
        {linkBad && <p className="mt-1 text-xs text-destructive">Links must start with http:// or https://</p>}
      </Field>
      <Field label="Instructions" htmlFor="pm-instructions">
        <Textarea
          id="pm-instructions"
          rows={4}
          value={form.instructions}
          onChange={(e) => set('instructions')(e.target.value)}
          placeholder={hints.instructions || 'Steps the customer follows to pay'}
        />
      </Field>
      <SwitchRow
        id="pm-active"
        label="Show to customers"
        hint="Switch off to hide these details and fall back to “we'll email you instructions”."
        checked={form.active}
        onChange={set('active')}
      />
      <div>
        <p className="mb-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">Preview</p>
        <PaymentInstructionsCard
          method={method?.value}
          amount={20}
          reference="GC-7K2QXM"
          setting={{ pay_to_c: form.payTo.trim(), link_c: link, instructions_c: form.instructions.trim() }}
        />
      </div>
    </FormDialog>
  );
}

export default function AdminPaymentMethods() {
  const { data, loading, error, run } = useFetch(loadPaymentSettings, []);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const methods = withSettings(data);
  const ready = methods.filter((m) => hasDetails(m.setting)).length;

  function edit(method) {
    setEditing(method);
    setOpen(true);
  }

  async function clear(method) {
    const res = await sdk.table('payment_settings_c').remove(method.setting.Id);
    if (!committed(res, `${method.label} details cleared`)) return false;
    run();
    return true;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Store"
        title="Payment methods"
        description="Where each payment method sends money. Customers see these details right after ordering and on their order page until it is paid."
      />

      <div className="mb-4 flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm">
        <ApperIcon name="Info" size={16} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-muted-foreground">
          {ready
            ? `${ready} of ${methods.length} methods show instructions. The rest tell customers you'll email them.`
            : "No method is set up yet, so customers are told you'll email payment instructions."}
        </p>
      </div>

      <Panel bodyClassName="py-1">
        <ListBody loading={loading} data={data} error={error} run={run} rows={5} isEmpty={false}>
          <ul className="divide-y divide-border">
            {methods.map((m) => {
              const ok = hasDetails(m.setting);
              return (
                <li key={m.value} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {m.label}
                      {ok ? (
                        <Badge variant="outline" className="border-success-border bg-success-muted text-success">
                          Showing
                        </Badge>
                      ) : m.setting ? (
                        <Badge variant="outline">Hidden</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          Not set up
                        </Badge>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {m.setting?.pay_to_c || m.setting?.link_c || 'No details saved'}
                    </p>
                    {m.setting?.ModifiedOn && (
                      <p className="text-xs text-muted-foreground">Updated {formatRelativeTime(m.setting.ModifiedOn)}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant={m.setting ? 'outline' : 'default'} size="sm" onClick={() => edit(m)}>
                      <ApperIcon name={m.setting ? 'Pencil' : 'Plus'} size={16} />
                      {m.setting ? 'Edit' : 'Set up'}
                    </Button>
                    {m.setting && (
                      <ConfirmDelete
                        label="Clear"
                        title={`Clear ${m.label} details?`}
                        description="Customers paying this way go back to “we'll email you instructions”."
                        onConfirm={() => clear(m)}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </ListBody>
      </Panel>

      <MethodDialog open={open} onOpenChange={setOpen} method={editing} onSaved={run} />
    </div>
  );
}
